import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useThunk } from "../../hooks/useThunk";
import { fetchTaskerFeed } from "../../store";
import Button from "../../components/Form/CustomButton";
import TaskerFeedJobCard from "../../components/Tasker/TaskerFeedJobCard";
import ExpandableText from "../../components/Form/ExpandableText";
import TimeCalculator from "../../components/Common/TimeCalculator";
import arrowLeft from "../../assets/icons/arrowLeftBlack.svg";
import EnhancedPhotoCarousel from "../../components/Common/EnhancedPhotoCarousel";
import { formatYYYY_MM_DD } from "../../utils/dateFormating";
import showToast from "../../utils/toastNotifications";
import ErrorDisplay from "../../error/ErrorDisplay";
import { createThread } from "../../store/thunks/threadThunks";
import { ErrorCodes } from "../../constants/errorCodes";
import TaskerFeedSkeleton from "../../components/skeleton/TaskerFeedSkeleton";
import ExploreJobCardSkeleton from "../../components/skeleton/ExploreJobCardSkeleton";
import TagButton from "../../components/Common/TagButton";

function TaskerFeed() {

  const navigate = useNavigate();

  const { jobId } = useParams();

  // Redux selectors
  const userState = useSelector(state => state?.auth?.user);
  const { jobPost: mainPost, matchingJobPosts = [] } = useSelector(
    (state) => state.jobPost?.taskerFeed || { matchingJobPosts: [] }
  );

  // useThunk call
  const [doFetchTaskerFeed, isFetchTakerFeed, fetchTaskerFeedError] = useThunk(fetchTaskerFeed);
  const [doCreateThread, isCreateThread, errorCreateThread] = useThunk(createThread);

  const locationText =
    mainPost?.location?.subCities?.length > 0
      ? `${mainPost?.location?.subCities[0]}, ${mainPost?.location?.city}`
      : mainPost?.location?.city;
  const images = mainPost?.images || [];
  const categoryItem = mainPost?.category?.categoryItem || "";
  const skills = mainPost?.category?.skills || [];
  const formattedDate = mainPost?.date ? formatYYYY_MM_DD(mainPost?.date) : "";
  const formattedTime = mainPost?.time ? mainPost?.time : "";

  useEffect(() => {
    if (jobId) {
      const fetchData = async () => {
        const result = await doFetchTaskerFeed({ jobPostId: jobId });
        if (!result?.success) {
          showToast("error", result?.error?.message);
        }
      };
      fetchData();
    }
  }, [jobId, doFetchTaskerFeed]);


  const handleRetry = () => {
    doFetchTaskerFeed({ jobPostId: jobId });
  };

  // FUNCTION: handle chat now button click
  const handleStartChat = async(user2Id) => {
    if(user2Id){
      const response = await doCreateThread(user2Id);
      if(response?.success){
        navigate(`/chat?thread=${response?.response?.data?._id}`)
      }else{
        if(response?.error?.customCode === ErrorCodes.DUPLICATE_ENTRIES.code){
          navigate(`/chat?thread=${response?.error?.message?.split(":")[1]?.toString()?.trim()}`)
        }else{
          showToast("error", response?.error?.message || "Error Occured");
        }
      }
    }else{
      showToast("error", "User ID Not Provided");
    }
  }

  return (
    <div className="w-full px-5 sm:px-[20px] md:px-[40px] lg:px-[100px] mt-12 mb-10">
      <div className="flex justify-end">
        <Button
          buttonText="Back"
          textColor="black"
          icon={<img src={arrowLeft} alt="Arrow Left" />}
          iconPosition="left"
          className="gap-2 px-[16px]"
          width="0"
          onClick={() => navigate(-1)}
        />
      </div>

      {/* Show loading state when fetching data */}
      {isFetchTakerFeed ? (
        <div>
          <TaskerFeedSkeleton />
          {/* Skeleton for chat now button */}
          <div className="py-3 px-6 rounded-b-[1.25rem] border-[1px] border-light-blue bg-white mb-10 lg:mb-[5.5rem] animate-pulse">
            <div className="flex items-center justify-center sm:justify-end">
            <div className="h-[49px] bg-gray-200 rounded-xl w-full md:w-[214px]"></div>
            </div>
          </div>
        </div>
      ) : fetchTaskerFeedError ? (
        <ErrorDisplay
          errorMessage={fetchTaskerFeedError?.message || "An error occurred"}
          buttonText="Try Again"
          onButtonClick={handleRetry}
        />
      ) : mainPost ? (
        <div key={mainPost?._id}>
          <div className="main-container mt-[12px] p-5 sm:p-[24px] md:p-[48px] flex flex-col space-y-[24px] rounded-t-[20px] border-[1px] border-[#DDE1E6] bg-[#fff]">
            <div className="flex flex-col heading lg:flex-row lg:justify-between">
              <div className="flex flex-col gap-1 mb-4 lg:mb-0">
                <p className="text-[#0F161E] font-inter text-level-4 sm:leading-8 sm:text-[32px] font-semibold break-words">
                  {/* {jobPost.data?.[0]?.jobPost?.[0]?.title}Plumbing Repair and Electrical Checkup */}
                  {mainPost?.title}
                </p>
                <p className="text-content font-inter text-[16px] font-normal leading-5">
                  {locationText}
                </p>
              </div>
              <p className="text-[var(--subtle-text)] font-inter text-[18px] font-semibold">
                <TimeCalculator date={mainPost?.updatedAt} />
              </p>
            </div>

            <EnhancedPhotoCarousel
              images={images}
              showNavigation={images?.length > 1}
              showThumbnails={images?.length > 1}
            />

            <div className="description flex flex-col mt-[36px]">
              <p className="text-content text-[16px] sm:text-[18px] font-inter font-semibold">
                {mainPost?.description ? "Description" : ""}
              </p>
              <ExpandableText text={mainPost?.description} />

              <div className="mt-[24px] flex flex-col sm:flex-row sm:gap-[87px] space-y-4 sm:space-y-0">
                <div className="space-y-[12px]">
                  <p className="text-content text-[18px] font-semibold font-inter">
                    Date
                  </p>
                  <p className="text-content text-[16px] font-normal font-inter">
                    {formattedDate}
                  </p>
                </div>
                <div className="space-y-[12px]">
                  <p className="text-content text-[18px] font-semibold font-inter">
                    Time
                  </p>
                  <p className="text-content text-[16px] font-normal font-inter">
                    {formattedTime}
                  </p>
                </div>
              </div>

              <div className="mt-[24px] flex flex-col sm:flex-row sm:gap-[52px] space-y-4 sm:space-y-0">
                <div className="space-y-[12px]">
                  <p className="text-content text-[18px] font-semibold font-inter">
                    Category
                  </p>
                  <TagButton title={categoryItem} />
                </div>
                <div className="space-y-[12px] ">
                  <p className="text-content text-[18px] font-semibold font-inter">
                    Skill
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {skills.map((skill, index) => (
                      <TagButton key={index} title={skill} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="py-3 px-6 rounded-b-[20px] border-[1px] border-[#DDE1E6] bg-[#fff] mb-10 lg:mb-[88px] border-t-0">
            <div className="flex items-center justify-center sm:justify-end">
              <Button
                buttonText="Chat Now"
                className="px-24 w-full sm:w-[281px] font-semibold font-inter leading-[17px] text-[#0F161E]"
                textColor="black"
                width="281px"
                loading={isCreateThread}
                onClick={() => {handleStartChat(mainPost?.createdBy?.toString())}}
                disabled={mainPost?.createdBy?.toString() === userState?._id?.toString()}
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* Mini job cards section */}
      {isFetchTakerFeed ? (
        <div className="p-5 sm:p-6 lg:p-9 flex flex-col mt-12 gap-5 sm:gap-6 lg:gap-9 rounded-[20px] border-[1px] border-[#DDE1E6] bg-[#fff]">
          <div className="flex flex-row justify-between">
            <p className="font-inter text-[14px] sm:text-[16px] font-medium text-[#222325]">
              Recent Tasks
            </p>
            <p className="font-inter text-[14px] sm:text-[16px] font-medium text-[#222325]">
              Most Relevant
            </p>
          </div>
          {/* Skeleton for job post card */}
          <div className="mt-6 space-y-6">
            {[1, 2, 3].map((idx) => (
              <ExploreJobCardSkeleton key={idx} />
            ))}
          </div>
        </div>
      ) : matchingJobPosts?.length > 0 ? (
        <div className="p-5 sm:p-6 lg:p-9 flex flex-col mt-12 gap-5 sm:gap-6 lg:gap-9 rounded-[20px] border-[1px] border-[#DDE1E6] bg-[#fff]">
          <div className="flex flex-row justify-between">
            <p className="font-inter text-[14px] sm:text-[16px] font-medium text-[#222325]">
              Recent Tasks
            </p>
            <p className="font-inter text-[14px] sm:text-[16px] font-medium text-[#222325]">
              Most Relevant
            </p>
          </div>

          <div className="flex flex-col gap-6">
            {matchingJobPosts.map((job, index) => (
              <TaskerFeedJobCard
                key={job?._id || index}
                title={job?.title}
                lastUpdatedDate={job?.updatedAt}
                image={job?.images?.[0]}
                location={job?.location?.subCities?.[0]}
                jobTasks={job?.category?.skills}
                date={job?.date}
                time={job?.time} //NOTE: Time format "10:00"
                createdBy={job?.createdBy}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="p-5 sm:p-6 lg:p-9 flex flex-col mt-12 gap-5 sm:gap-6 lg:gap-9 rounded-[20px] border-[1px] border-[#DDE1E6] bg-[#fff]">
          <div className="flex flex-col items-center justify-center py-8">
            <p className="font-inter text-[16px] font-medium text-[#222325] mb-2">
              No tasks available
            </p>
            <p className="font-inter text-[14px] text-[#6F7174] text-center">
              There are currently no matching tasks. Check back later.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskerFeed;
